import { Button, Checkbox, Icon, token } from "@may-i/mash-design-system";
import * as RadixPopover from "@radix-ui/react-popover";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

export interface CameraFilterFlags {
  includeAnalysis: boolean;
  includeExcludeAnalysis: boolean;
  includeShown: boolean;
  includeNotShown: boolean;
  // 카메라 변경 상태
  includeNotChanged: boolean;
  includeAdjusted: boolean;
  includeAdded: boolean;
  // 이벤트 소스 타입
  includeLines: boolean;
  includeZones: boolean;
  includeNoEventSource: boolean;
}

export interface CameraFilterCounts {
  analysis: number;
  excludeAnalysis: number;
  shown: number;
  noFloorPlan: number;
  notChanged: number;
  adjusted: number;
  added: number;
  lines: number;
  zones: number;
  noEventSource: number;
}

export const DEFAULT_FILTER_FLAGS: CameraFilterFlags = {
  includeAnalysis: true,
  includeExcludeAnalysis: true,
  includeShown: true,
  includeNotShown: true,
  includeNotChanged: true,
  includeAdjusted: true,
  includeAdded: true,
  includeLines: true,
  includeZones: true,
  includeNoEventSource: true,
};

interface FilterPopoverProps {
  flags: CameraFilterFlags;
  counts: CameraFilterCounts;
  filteredCount: number;
  onApply: (flags: CameraFilterFlags) => void;
}

export function FilterPopover({ flags, counts, filteredCount, onApply }: FilterPopoverProps) {
  const { t } = useTranslation("camera-mapping");
  const [open, setOpen] = useState(false);
  const [local, setLocal] = useState<CameraFilterFlags>(flags);
  // 카메라 변경 상태는 자주 안 쓰여 아코디언으로 접어둠 (기본 접힘)
  const [changeStatusOpen, setChangeStatusOpen] = useState(false);

  const handleOpenChange = (next: boolean) => {
    if (next) setLocal(flags);
    setOpen(next);
  };

  const set = <K extends keyof CameraFilterFlags>(key: K, value: boolean) => {
    setLocal((prev) => ({ ...prev, [key]: value }));
  };

  const handleReset = () => setLocal(DEFAULT_FILTER_FLAGS);

  const handleCancel = () => {
    setLocal(flags);
    setOpen(false);
  };

  const handleApply = () => {
    onApply(local);
    setOpen(false);
  };

  // 활성 필터 요약 — 차원에서 일부만 선택됐을 때만 칩으로 표시 (모두 ON 이면 미표시)
  const summary: string[] = [];
  if (flags.includeAnalysis !== flags.includeExcludeAnalysis) {
    summary.push(flags.includeAnalysis ? t("filter.analysis") : t("filter.excludeAnalysis"));
  }
  if (flags.includeShown !== flags.includeNotShown) {
    summary.push(flags.includeShown ? t("filter.shown") : t("filter.noFloorPlan"));
  }
  if (!(flags.includeNotChanged && flags.includeAdjusted && flags.includeAdded)) {
    const parts = [
      flags.includeNotChanged && t("filter.notChanged"),
      flags.includeAdjusted && t("filter.adjusted"),
      flags.includeAdded && t("filter.added"),
    ].filter(Boolean) as string[];
    if (parts.length > 0) summary.push(parts.join("/"));
  }
  if (!(flags.includeLines && flags.includeZones && flags.includeNoEventSource)) {
    const parts = [
      flags.includeLines && t("filter.line"),
      flags.includeZones && t("filter.zone"),
      flags.includeNoEventSource && t("filter.noEventSource"),
    ].filter(Boolean) as string[];
    if (parts.length > 0) summary.push(parts.join("/"));
  }
  const summaryText = summary.length > 0 ? summary.join(" · ") : t("filter.summaryAll");
  const isFiltering = summary.length > 0;

  return (
    <RadixPopover.Root open={open} onOpenChange={handleOpenChange}>
      <RadixPopover.Trigger asChild>
        <TriggerButton type="button" $active={isFiltering}>
          <Icon icon="ic_filter" width={14} height={14} />
          <TriggerLabel>{summaryText}</TriggerLabel>
          <TriggerCount $active={isFiltering}>{filteredCount}</TriggerCount>
        </TriggerButton>
      </RadixPopover.Trigger>
      <RadixPopover.Portal>
        <Content align="start" sideOffset={4}>
          <Header>
            <HeaderTitle>{t("filter.title")}</HeaderTitle>
            <Button variant="text" color="primary" size="xSmall" onClick={handleReset}>
              {t("filter.reset")}
            </Button>
          </Header>

          <Section>
            <SectionTitle>{t("filter.analysisLabel")}</SectionTitle>
            <CheckboxRow>
              <Checkbox
                size="small"
                color="primary"
                label={`${t("filter.analysis")}(${counts.analysis})`}
                checked={local.includeAnalysis}
                onCheckedChange={(v) => typeof v === "boolean" && set("includeAnalysis", v)}
              />
              <Checkbox
                size="small"
                color="primary"
                label={`${t("filter.excludeAnalysis")}(${counts.excludeAnalysis})`}
                checked={local.includeExcludeAnalysis}
                onCheckedChange={(v) => typeof v === "boolean" && set("includeExcludeAnalysis", v)}
              />
            </CheckboxRow>
          </Section>

          <Section>
            <SectionTitle>{t("filter.floorPlanLabel")}</SectionTitle>
            <CheckboxRow>
              <Checkbox
                size="small"
                color="primary"
                label={`${t("filter.shown")}(${counts.shown})`}
                checked={local.includeShown}
                onCheckedChange={(v) => typeof v === "boolean" && set("includeShown", v)}
              />
              <Checkbox
                size="small"
                color="primary"
                label={`${t("filter.noFloorPlan")}(${counts.noFloorPlan})`}
                checked={local.includeNotShown}
                onCheckedChange={(v) => typeof v === "boolean" && set("includeNotShown", v)}
              />
            </CheckboxRow>
          </Section>

          <Section>
            <SectionTitle>{t("filter.eventSourceLabel")}</SectionTitle>
            <CheckboxRow>
              <CheckboxCell>
                <Checkbox
                  size="small"
                  color="primary"
                  label={`${t("filter.line")}(${counts.lines})`}
                  checked={local.includeLines}
                  onCheckedChange={(v) => typeof v === "boolean" && set("includeLines", v)}
                />
              </CheckboxCell>
              <CheckboxCell>
                <Checkbox
                  size="small"
                  color="primary"
                  label={`${t("filter.zone")}(${counts.zones})`}
                  checked={local.includeZones}
                  onCheckedChange={(v) => typeof v === "boolean" && set("includeZones", v)}
                />
              </CheckboxCell>
              <CheckboxCell>
                <Checkbox
                  size="small"
                  color="primary"
                  label={`${t("filter.noEventSource")}(${counts.noEventSource})`}
                  checked={local.includeNoEventSource}
                  onCheckedChange={(v) => typeof v === "boolean" && set("includeNoEventSource", v)}
                />
              </CheckboxCell>
            </CheckboxRow>
          </Section>

          <Section>
            <AccordionHeader
              type="button"
              onClick={() => setChangeStatusOpen((v) => !v)}
              aria-expanded={changeStatusOpen}
            >
              <SectionTitle>{t("filter.changeStatusLabel")}</SectionTitle>
              <Icon icon={changeStatusOpen ? "ic_chevron_up" : "ic_chevron_down"} width={16} height={16} />
            </AccordionHeader>
            {changeStatusOpen && (
              <CheckboxRow $alignTop>
                <CheckboxCell>
                  <Checkbox
                    size="small"
                    color="primary"
                    label={`${t("filter.notChanged")}(${counts.notChanged})`}
                    checked={local.includeNotChanged}
                    onCheckedChange={(v) => typeof v === "boolean" && set("includeNotChanged", v)}
                  />
                </CheckboxCell>
                <CheckboxCell>
                  <CheckboxColumn>
                    <Checkbox
                      size="small"
                      color="primary"
                      label={t("filter.changed")}
                      checked={local.includeAdjusted && local.includeAdded}
                      onCheckedChange={(v) => {
                        if (typeof v !== "boolean") return;
                        setLocal((prev) => ({ ...prev, includeAdjusted: v, includeAdded: v }));
                      }}
                    />
                    <SubItem>
                      <Checkbox
                        size="small"
                        color="primary"
                        label={`${t("filter.adjusted")}(${counts.adjusted})`}
                        checked={local.includeAdjusted}
                        onCheckedChange={(v) => typeof v === "boolean" && set("includeAdjusted", v)}
                      />
                    </SubItem>
                    <SubItem>
                      <Checkbox
                        size="small"
                        color="primary"
                        label={`${t("filter.added")}(${counts.added})`}
                        checked={local.includeAdded}
                        onCheckedChange={(v) => typeof v === "boolean" && set("includeAdded", v)}
                      />
                    </SubItem>
                  </CheckboxColumn>
                </CheckboxCell>
              </CheckboxRow>
            )}
          </Section>

          <Footer>
            <Button variant="outlined" color="primary" size="xSmall" onClick={handleCancel}>
              {t("filter.cancel")}
            </Button>
            <ContainedButton variant="contained" color="primary" size="xSmall" onClick={handleApply}>
              {t("filter.apply")}
            </ContainedButton>
          </Footer>
        </Content>
      </RadixPopover.Portal>
    </RadixPopover.Root>
  );
}

const TriggerButton = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 10px;
  border-radius: 6px;
  border: 1px solid ${({ $active }) => ($active ? "#484BD2" : "rgba(143, 149, 178, 0.32)")};
  background: ${({ $active }) => ($active ? "rgba(72, 75, 210, 0.08)" : "#fff")};
  color: ${({ $active }) => ($active ? "#484BD2" : token("colors.text.primary"))};
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.5;
  cursor: pointer;
  min-width: 0;

  &:hover {
    background: ${({ $active }) => ($active ? "rgba(72, 75, 210, 0.14)" : token("colors.action.hover"))};
  }
`;

const TriggerLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TriggerCount = styled.span<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 16px;
  padding: 0 4px;
  border-radius: 8px;
  background: ${({ $active }) => ($active ? "#484BD2" : "rgba(143, 149, 178, 0.24)")};
  color: ${({ $active }) => ($active ? "#fff" : token("colors.text.secondary"))};
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  flex-shrink: 0;
`;

const Content = styled(RadixPopover.Content)`
  width: 320px;
  background: #fff;
  border-radius: 12px;
  box-shadow:
    -20px 20px 40px -4px rgba(105, 111, 140, 0.24),
    0px 0px 2px 0px rgba(105, 111, 140, 0.16);
  z-index: 50;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 16px 16px 0;
`;

const HeaderTitle = styled.span`
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.5;
  color: #11111f;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 16px 0;
`;

const AccordionHeader = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #696f8c;
`;

const SectionTitle = styled.span`
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.5;
  color: #696f8c;
`;

const CheckboxRow = styled.div<{ $alignTop?: boolean }>`
  display: flex;
  gap: 8px;
  ${({ $alignTop }) => $alignTop && "align-items: flex-start;"}
`;

const CheckboxColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

// 균등 폭 컬럼 셀 — 2열/3열 정렬용
const CheckboxCell = styled.div`
  flex: 1 1 0;
  min-width: 0;
`;

const SubItem = styled.div`
  padding-left: 20px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  padding: 16px;
`;

// 뿌리 원인: DS Button 의 contained 는 border:none, outlined 는 border:1px (box-sizing 비-border-box)
// → outlined 만 ~2px 더 커짐(width·height). contained 에 동일하게 1px 투명 테두리를 예약해 크기 일치.
const ContainedButton = styled(Button)`
  && {
    border: 1px solid transparent;
  }
`;
